/**
 * APX Red Teaming Model v2
 * Three lenses × Four anchor questions
 * Cynical CFO | Resistant Client | Aggressive Competitor
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Printer, Share2, Lightbulb, Loader2, RotateCcw, ChevronDown } from "lucide-react";
import { openRedTeamV2Print } from "@/lib/redteam-v2-print";
import DashboardLayout from "@/components/DashboardLayout";

const SECTORS = [
  "General",
  "Restaurant",
  "Bodega / Small Market",
  "Trucking",
  "Automotive",
  "Real Estate",
  "General Contracting",
  "Internal (APX Operations)",
];

const ANCHOR_QUESTIONS = [
  "Where am I exposed?",
  "What is the real bottleneck?",
  "What am I assuming that may be wrong?",
  "What should I fix before I make my next big move?",
];

const LENS_DOT_COLORS = ["#22c55e", "#3b82f6", "#f97316"];

const LENSES = [
  {
    dot: "#22c55e",
    name: "Cynical CFO",
    tag: "Financial Reality",
    description:
      "Challenges unit economics, margin assumptions, capacity constraints, and hidden costs. Exposes the weakest financial assumptions before they become real losses.",
  },
  {
    dot: "#3b82f6",
    name: "Resistant Client",
    tag: "Demand & Trust Reality",
    description:
      "Responds as a skeptical, price-sensitive business owner who has been burned before. Surfaces the real objections, fears, and trust gaps that kill deals.",
  },
  {
    dot: "#f97316",
    name: "Aggressive Competitor",
    tag: "Market & Execution Reality",
    description:
      "Analyzes how a well-resourced rival would exploit weaknesses in your idea. Identifies market vulnerabilities, execution gaps, and positioning blind spots.",
  },
];

const WHAT_YOU_GET = [
  { number: "12", label: "Unique perspectives", sub: "3 lenses × 4 anchor questions" },
  { number: "12", label: "Mitigation recommendations", sub: "One per perspective" },
  { number: "1", label: "Printable analysis report", sub: "PDF-ready, professional format" },
  { number: "∞", label: "Analysis history", sub: "Every run saved for review" },
];

const EXAMPLES = [
  {
    text: "A fast-casual restaurant in a mid-size city (pop. 200K) that serves rotating seasonal menus sourced from 3–5 local farms. No freezers on premise. Menu changes weekly. Average ticket: $18. Target: 120 covers/day at launch, 200 within 18 months.",
    sector: "Restaurant",
  },
  {
    text: "A major food delivery platform raises its commission rate to 35% — how does this affect restaurant cash flow and our fee justification?",
    sector: "Restaurant",
  },
  {
    text: "A large grocery chain opens two locations within a mile of three bodega clients — stress test their revenue assumptions.",
    sector: "Bodega / Small Market",
  },
  {
    text: "A shop owner retires with no succession plan — how does APX identify at-risk clients early and what is the advisory play?",
    sector: "Automotive",
  },
];

type RedTeamV2Result = {
  idea: string;
  sector: string;
  lenses: Array<{
    name: string;
    subtitle: string;
    questions: Array<{
      question: string;
      response: string;
      mitigation: string;
    }>;
  }>;
};

function cleanText(text: string): string {
  return (text || "")
    .replace(/\*\*/g, "")
    .replace(/\*/g, "")
    .replace(/#{1,6}\s/g, "")
    .trim();
}

function parseBullets(text: string): string[] {
  const t = cleanText(text);
  if (!t) return [];
  const lines = t.split("\n").map((l) => l.trim()).filter(Boolean);
  const bullets: string[] = [];
  for (const line of lines) {
    const stripped = line.replace(/^[\d]+[.)]\s*/, "").replace(/^[-•–]\s*/, "").trim();
    if (stripped) bullets.push(stripped);
  }
  return bullets.length > 0 ? bullets : [t];
}

function QuestionSection({
  question,
  response,
  mitigation,
}: {
  question: string;
  response: string;
  mitigation: string;
}) {
  const bullets = parseBullets(mitigation);
  const responseBullets = parseBullets(response);

  return (
    <div className="mb-6">
      <div className="text-sm font-bold text-gray-900 mb-1">{question}</div>
      <div className="text-[10px] font-semibold tracking-widest uppercase text-gray-400 mb-2">
        Analysis
      </div>
      <ul className="list-disc pl-4 space-y-1.5 mb-3">
        {responseBullets.map((b, i) => (
          <li key={i} className="text-sm leading-snug text-gray-800">
            {b}
          </li>
        ))}
      </ul>
      {mitigation && (
        <div className="border border-gray-300 rounded-md p-3">
          <div className="text-[10px] font-bold tracking-widest uppercase text-gray-600 mb-2">
            Mitigation
          </div>
          <ul className="list-disc pl-4 space-y-1.5">
            {bullets.map((b, i) => (
              <li key={i} className="text-sm leading-snug text-gray-800">
                {b}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function LensCard({
  lens,
  dotColor,
}: {
  lens: RedTeamV2Result["lenses"][0];
  dotColor: string;
}) {
  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-white">
      <div className="flex items-start gap-2 mb-4 pb-3 border-b border-gray-100">
        <div
          className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
          style={{ backgroundColor: dotColor }}
        />
        <div>
          <div className="text-base font-bold text-gray-900">{lens.name}</div>
          <div className="text-sm text-gray-500 mt-0.5">{lens.subtitle}</div>
        </div>
      </div>
      {ANCHOR_QUESTIONS.map((q, qi) => {
        const cell = lens.questions[qi];
        return (
          <QuestionSection
            key={qi}
            question={q}
            response={cell?.response ?? ""}
            mitigation={cell?.mitigation ?? ""}
          />
        );
      })}
    </div>
  );
}

export default function RedTeamV2() {
  const [idea, setIdea] = useState("");
  const [sector, setSector] = useState("General");
  const [result, setResult] = useState<RedTeamV2Result | null>(null);
  const [showFramework, setShowFramework] = useState(true);

  const mutation = trpc.intelligence.redTeamV2.useMutation({
    onSuccess: (data) => {
      setResult(data as RedTeamV2Result);
      setShowFramework(false);
      toast.success("Red Team analysis complete");
    },
    onError: (err) => {
      const msg = err.message ?? "";
      if (msg.toLowerCase().includes("rate") || msg.toLowerCase().includes("limit")) {
        toast.error("Rate limited — please wait 30 seconds and try again.");
      } else if (msg.toLowerCase().includes("overload")) {
        toast.error("AI service is overloaded. Please try again in a moment.");
      } else {
        toast.error(`Analysis failed: ${msg}`);
      }
    },
  });

  const handleRun = () => {
    if (!idea.trim() || idea.trim().length < 10) {
      toast.error("Please enter a scenario or idea (at least 10 characters).");
      return;
    }
    setResult(null);
    mutation.mutate({ idea: idea.trim(), sector });
  };

  const handleClear = () => {
    setIdea("");
    setResult(null);
    setSector("General");
    setShowFramework(true);
  };

  const handleExportText = () => {
    if (!result) return;
    const lines: string[] = [
      "APX RED TEAMING ANALYSIS",
      `Date: ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
      `Sector: ${result.sector}`,
      "",
      `STRATEGIC QUESTION: "${result.idea}"`,
      "",
      "=".repeat(80),
      "",
    ];
    ANCHOR_QUESTIONS.forEach((aq, qi) => {
      lines.push(`ANCHOR QUESTION ${qi + 1}: ${aq.toUpperCase()}`);
      lines.push("-".repeat(60));
      result.lenses.forEach((lens) => {
        lines.push(`\n[${lens.name.toUpperCase()} — ${lens.subtitle}]`);
        lines.push(lens.questions[qi]?.response ?? "");
        if (lens.questions[qi]?.mitigation) {
          lines.push("\nMITIGATION RECOMMENDATION:");
          lines.push(lens.questions[qi].mitigation);
        }
      });
      lines.push("\n" + "=".repeat(80) + "\n");
    });
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `APX-RedTeam-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Text exported");
  };

  const handlePrint = () => {
    if (!result) return;
    openRedTeamV2Print(result);
  };

  const handleShare = () => {
    if (!result) return;
    const text = `APX Red Team Analysis\n\nIdea: ${result.idea}\nSector: ${result.sector}\n\nGenerated ${new Date().toLocaleDateString()}`;
    if (navigator.share) {
      navigator.share({ title: "APX Red Team Analysis", text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => toast.success("Summary copied to clipboard"));
    }
  };

  return (
    <DashboardLayout>
      <>
      {/* ── Page Header ── */}
      <div className="bg-[#dce9f0] rounded-xl px-4 py-3 mb-3">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="https://d2xsxph8kpxj0f.cloudfront.net/310419663029793542/5GqF8Kn9QcwXve8h8refaY/apx-logo-final_808b0f18.png"
                alt="APX Logo"
                className="w-8 h-8 object-contain"
              />
              <div>
                <div className="text-sm font-bold tracking-widest uppercase text-[#4a6b7c]">APX Advisory Framework</div>
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight leading-tight">Red Teaming Analysis</h1>
              </div>
            </div>
            <p className="text-lg text-[#4a6b7c] hidden sm:block">Strategic assessment through three adversarial lenses</p>
          </div>
          {result && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={handleExportText} className="gap-2">
                <Download className="w-4 h-4" /> Export
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
                <Printer className="w-4 h-4" /> Print / PDF
              </Button>
              <Button variant="outline" size="sm" onClick={handleShare} className="gap-2">
                <Share2 className="w-4 h-4" /> Share
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="pb-4 space-y-3 max-w-4xl mx-auto">

        {/* ── Framework Overview (collapsible after first run) ─────────── */}
        {showFramework && (
          <div className="space-y-3">

            {/* Three Lenses + Four Questions — single compact card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
                {LENSES.map((lens) => (
                  <div key={lens.name} className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: lens.dot }} />
                      <span className="text-lg font-bold text-gray-900">{lens.name}</span>
                    </div>
                    <div className="text-sm font-semibold uppercase tracking-wider mt-0.5" style={{ color: lens.dot }}>{lens.tag}</div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2">
                <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
                  {ANCHOR_QUESTIONS.map((q, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-400 w-3">{i + 1}</span>
                      <span className="text-base font-medium text-gray-700">{q}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toggle to show/hide framework after a run */}
        {!showFramework && (
          <button
            onClick={() => setShowFramework(true)}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ChevronDown className="w-3.5 h-3.5" />
            Show framework overview
          </button>
        )}

        {/* ── Input Card ──────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border-2 border-black shadow-sm p-4">
          {/* Examples — compact single row */}
          <div className="mb-2">
            <p className="text-sm text-gray-400 mb-1 font-semibold uppercase tracking-wider">Examples — click to load</p>
            <div className="flex flex-col gap-1.5">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => { setIdea(ex.text); setSector(ex.sector); }}
                  className="text-sm text-left px-3 py-2 rounded bg-gray-50 hover:bg-gray-100 border border-gray-200 transition-colors w-full truncate"
                  title={ex.text}
                >
                  {ex.text.length > 120 ? ex.text.slice(0, 120) + "…" : ex.text}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1 space-y-3">
              <Textarea
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                placeholder="Describe the business idea, decision, or scenario to stress-test..."
                className="min-h-[120px] text-lg resize-none"
                maxLength={3000}
              />
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="text-base text-gray-500 shrink-0">Sector:</span>
                  <Select value={sector} onValueChange={setSector}>
                    <SelectTrigger className="w-52 h-9 text-base">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map((s) => (
                        <SelectItem key={s} value={s} className="text-sm">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="text-base text-gray-400">{idea.length}/3000</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={handleClear} className="gap-1 text-sm">
                    <RotateCcw className="w-3 h-3" /> Clear
                  </Button>
                  <Button
                    onClick={handleRun}
                    disabled={mutation.isPending || idea.trim().length < 10}
                    size="sm"
                    className="bg-gray-900 hover:bg-gray-800 text-white gap-2"
                  >
                    {mutation.isPending ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing…</>
                    ) : (
                      "Run Red Team Analysis"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>


        </div>

        {/* ── Loading State ────────────────────────────────────────────── */}
        {mutation.isPending && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-10 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-700 mx-auto mb-3" />
            <p className="text-lg font-semibold text-gray-900">Running 12 parallel analyses…</p>
            <p className="text-base text-gray-500 mt-1">
              3 lenses × 4 anchor questions. This takes 15–30 seconds.
            </p>
          </div>
        )}

        {/* ── Results ─────────────────────────────────────────────────── */}
        {result && (
          <div className="space-y-5">

            {/* Strategic Question Box */}
            <div className="bg-gray-50 border-2 border-black rounded-xl p-5">
              <div className="text-sm font-bold tracking-widest uppercase text-gray-500 mb-3">
                Strategic Question
              </div>
              <p className="text-lg leading-relaxed text-gray-900">{result.idea}</p>
            </div>

            {/* ── Row-by-Question Layout ─────────────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

              {/* Lens Header Row */}
              <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-200">
                {result.lenses.map((lens, li) => (
                  <div key={li} className="px-5 py-4 flex items-start gap-2.5"
                    style={{ backgroundColor: li === 0 ? "#f0fdf4" : li === 1 ? "#eff6ff" : "#fff7ed" }}>
                    <div
                      className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: LENS_DOT_COLORS[li] }}
                    />
                    <div>
                      <div className="text-lg font-bold text-gray-900">{lens.name}</div>
                      <div className="text-base text-gray-500 mt-0.5">{lens.subtitle}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* One row per anchor question */}
              {ANCHOR_QUESTIONS.map((question, qi) => (
                <div key={qi} className={qi < ANCHOR_QUESTIONS.length - 1 ? "border-b border-gray-100" : ""}>

                  {/* Question label — full width */}
                  <div className="px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                    <span className="text-base font-bold text-gray-700 uppercase tracking-wider">
                      Q{qi + 1} &mdash;
                    </span>
                    <span className="text-base font-semibold text-gray-800 ml-1.5">{question}</span>
                  </div>

                  {/* 3-column response + mitigation */}
                  <div className="grid grid-cols-3 divide-x divide-gray-100">
                    {result.lenses.map((lens, li) => {
                      const cell = lens.questions[qi];
                      const responseBullets = parseBullets(cell?.response ?? "");
                      const mitigationBullets = parseBullets(cell?.mitigation ?? "");
                      return (
                        <div key={li} className="px-5 py-4 space-y-3">
                          {/* Response */}
                          <div>
                            <div className="text-sm font-semibold tracking-widest uppercase text-gray-400 mb-1.5">
                              Analysis
                            </div>
                            <ul className="space-y-1.5">
                              {responseBullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-1.5 text-lg leading-snug text-gray-800">
                                  <span
                                    className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                                    style={{ backgroundColor: LENS_DOT_COLORS[li] }}
                                  />
                                  {b}
                                </li>
                              ))}
                            </ul>
                          </div>
                          {/* Mitigation */}
                          {cell?.mitigation && (
                            <div className="border border-gray-200 rounded-md p-3">
                              <div className="text-sm font-bold tracking-widest uppercase text-gray-500 mb-1.5">
                                Mitigation
                              </div>
                              <ul className="space-y-1.5">
                                {mitigationBullets.map((b, i) => (
                                  <li key={i} className="flex items-start gap-1.5 text-lg leading-snug text-gray-700">
                                    <span
                                      className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                                      style={{ backgroundColor: LENS_DOT_COLORS[li] }}
                                    />
                                    {b}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

            </div>

            {/* Bottom Action Bar */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 flex items-center justify-between flex-wrap gap-3">
              <p className="text-base text-gray-400">
                Results are AI-generated and should be reviewed critically before acting on them.
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExportText} className="gap-2">
                  <Download className="w-4 h-4" /> Export Text
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2">
                  <Printer className="w-4 h-4" /> Print / PDF
                </Button>
                <Button variant="outline" size="sm" onClick={handleShare} className="gap-2">
                  <Share2 className="w-4 h-4" /> Share
                </Button>
              </div>
            </div>
          </div>
        )}

      </div>
      </>
    </DashboardLayout>
  );
}
